import { useTransition } from 'react';
import { Button, Col, Container, Divider, FilterChips, Row } from '@geberit/gdds';
import type { SelectOption } from '@geberit/gdds/dist/esm/form/Dropdown/types/Dropdown.types';

// styles
import styles from './tiles-filter.module.scss';

// utils
import { buildSize, gridSizes } from 'utils/gridSize';
import { gridSize } from '../sizes';
import { buildPrefixedFilterIdentifier, FilterDropdown } from './filter-dropdown';
import { useSearchParamsState } from 'utils/hooks/use-search-params-state';
import { isDefined } from 'utils/ts-utilities';
import { useTranslationFunction } from 'utils/hooks/use-translations';
import { isExists } from 'utils/is-empty';

export type FilterElement = {
  label: string;
  placeholder: string;
  selectedText: string;
  selectedTextPlural: string;
  items: SelectOption[];
};

export type SelectableElements = 'products' | 'typeOfBuilding' | 'region';

type TilesFilterProps = {
  filterElements: Record<SelectableElements, FilterElement>;
  fsSectionId: string;
};

export function TilesFilter({ filterElements, fsSectionId }: Readonly<TilesFilterProps>) {
  const [_isPending, setStartTransition] = useTransition();
  const translate = useTranslationFunction();
  const filterNames = Object.keys(filterElements).map((ele) =>
    buildPrefixedFilterIdentifier(fsSectionId, ele),
  ) as SelectableElements[];
  const [searchParamsState, _, setMultipleSelectedParams] = useSearchParamsState(
    filterNames,
    [],
    true,
  );

  const gridWidth = gridSizes[gridSize['NORD']];

  const chips = Object.entries(searchParamsState)
    .map(([filterName, filterValues]) => {
      return filterValues.map((value) => ({
        title: translate(filterElements[filterName.replace(`${fsSectionId}_`, '')].label),
        value: translate(value),
      }));
    })
    .flat();

  const shouldShowFilters = isExists(Object.values(filterElements).flatMap((ele) => ele.items));
  if (!shouldShowFilters) {
    return null;
  }

  return (
    <Container maxContentWidth={buildSize(gridWidth)} className={styles['tiles-filters-wrapper']}>
      <Row>
        <Col>
          <div className={styles['filters-wrapper']}>
            <span className={styles['filter-label']}>{translate('filter_label')}</span>
            <Divider />
            <div className={styles['filters']}>
              {Object.entries(filterElements).map(([filterName, FilterData]) => {
                return (
                  <FilterDropdown
                    fsSectionId={fsSectionId}
                    key={filterName}
                    id={`tiles-filter-${FilterData.label}-${fsSectionId}`}
                    label={FilterData.label}
                    allItems={FilterData.items}
                    placeholder={FilterData.placeholder}
                    selectedText={FilterData.selectedText}
                    selectedTextPlural={FilterData.selectedTextPlural}
                    filterName={filterName as SelectableElements}
                  />
                );
              })}
            </div>

            <div className={styles['filters-chips-wrapper']}>
              <FilterChips
                selectedItems={Object.values(searchParamsState).flat().filter(isDefined)}
                gap={16}
                onChange={(selectedItems) => {
                  setStartTransition(() => {
                    const newSearchParams = Object.entries(searchParamsState).reduce(
                      (acc, [filterName, filterValues]) => {
                        const newFilterValues = filterValues
                          ?.filter((item) => selectedItems.includes(item))
                          ?.filter(isDefined);
                        if (newFilterValues) acc[filterName] = newFilterValues;
                        return acc;
                      },
                      {} as Record<string, string[]>,
                    );
                    setMultipleSelectedParams(newSearchParams);
                  });
                }}
                type="A"
                isShowLabel
                chips={chips}
              />
            </div>

            <div className={styles['reset-button-wrapper']}>
              <Button
                onClick={() =>
                  setStartTransition(() => {
                    setMultipleSelectedParams(
                      Object.fromEntries(filterNames.map((name) => [name, []])),
                    );
                  })
                }
                stylingType="flatHighlight"
              >
                {translate('filter_resetfilter')}
              </Button>
            </div>
            <Divider />
          </div>
        </Col>
      </Row>
    </Container>
  );
}
